import { useEffect, useState } from "react";
import { Copy, Mail, MessageSquare, Share2, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const INVITE_COPY = (link) =>
  `🐼 Join me on MyPandaX!
It’s an inclusive 18+ social community where you can go live, meet new people, join communities, message members and support creators with virtual gifts.
Create your profile and join the Panda Pack today:
${link}
#MyPandaX #PandaPack #LiveStreaming #SocialCommunity #ContentCreators`;

export function ShareInviteModal({ open, onOpenChange }) {
  const [link, setLink] = useState("");

  useEffect(() => {
    if (open && !link) {
      api.get("/referrals/me").then(({ data }) => setLink(data.link)).catch(() => {});
    }
  }, [open, link]);

  const text = INVITE_COPY(link || "https://mypandax.com/join");
  const enc = encodeURIComponent;

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Join me on MyPandaX", text, url: link });
        return true;
      } catch { /* user cancelled */ }
    }
    return false;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Invite copied — paste it anywhere!");
    } catch {
      toast.error("Copy failed — long-press the link to copy.");
    }
  };

  const targets = [
    { key: "sms", label: "Text Message", icon: MessageSquare, href: `sms:?&body=${enc(text)}` },
    { key: "email", label: "Email", icon: Mail, href: `mailto:?subject=${enc("Join me on MyPandaX 🐼")}&body=${enc(text)}` },
    { key: "facebook", label: "Facebook", emoji: "f", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(link)}&quote=${enc(text)}` },
    { key: "x", label: "X", emoji: "𝕏", href: `https://twitter.com/intent/tweet?text=${enc(text)}` },
    { key: "whatsapp", label: "WhatsApp", emoji: "🟢", href: `https://wa.me/?text=${enc(text)}` },
    { key: "snapchat", label: "Snapchat", emoji: "👻", href: `https://www.snapchat.com/scan?attachmentUrl=${enc(link)}` },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-invite-modal" className="bg-zinc-950 border-zinc-800 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">🐼 Build the Panda Pack</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-400 -mt-2">
          Invite friends with your personal link. Qualified referrals earn Pack badges and featured placement.
        </p>
        <div data-testid="share-invite-link" className="bg-black/40 border border-white/10 px-3 py-2.5 text-xs text-rose-300 break-all select-all">
          {link || "Loading your link…"}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button data-testid="share-native-btn" onClick={async () => { if (!(await nativeShare())) copyLink(); }}
            className="btn-crimson rounded-none col-span-1">
            <Share2 size={14} className="mr-1.5" /> Share
          </Button>
          <Button data-testid="share-copy-btn" onClick={copyLink} variant="ghost"
            className="rounded-none border border-white/15 hover:bg-white/5">
            <Copy size={14} className="mr-1.5" /> Copy Link
          </Button>
          {targets.map((t) => (
            <a key={t.key} href={t.href} target={t.key === "sms" || t.key === "email" ? "_self" : "_blank"}
              rel="noopener noreferrer" data-testid={`share-${t.key}-btn`}
              className="flex items-center justify-center gap-2 border border-white/10 py-2.5 text-xs text-zinc-300 hover:bg-white/5 transition-colors">
              {t.icon ? <t.icon size={14} /> : <span className="text-sm leading-none">{t.emoji}</span>}
              {t.label}
            </a>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function InviteFriendsButton({ className = "", variant = "solid", testid = "invite-friends-btn" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen(true)}
        className={variant === "solid"
          ? `inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-500 text-white text-xs uppercase tracking-widest px-4 py-2.5 transition-colors ${className}`
          : `inline-flex items-center gap-2 border border-rose-600/50 text-rose-300 hover:bg-rose-600/10 text-xs uppercase tracking-widest px-4 py-2.5 transition-colors ${className}`}
      >
        <Share2 size={13} /> Invite Friends
      </button>
      <ShareInviteModal open={open} onOpenChange={setOpen} />
    </>
  );
}
